import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  useTheme,
  useMediaQuery,
  Divider,
  Switch,
  Button
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

interface ProfileStats {
  totalEarnings: number;
  amountPaidOut: number;
  pendingPayouts: number;
}

const Profile: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: stats, isLoading } = useQuery<ProfileStats>({
    queryKey: ['profileStats'],
    queryFn: async () => {
      // TODO: Implement actual API call
      return {
        totalEarnings: 250.00,
        amountPaidOut: 200.00,
        pendingPayouts: 50.00,
      };
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Financial Overview
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h4" color="primary">
                ${stats?.totalEarnings}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Total Earnings
              </Typography>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h4" color="success.main">
                ${stats?.amountPaidOut}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Amount Paid Out
              </Typography>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h4" color="warning.main">
                ${stats?.pendingPayouts}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Pending Payouts
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Personal Details
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    John D. Smith
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1">
                    January 15, 2024
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                Contact Information
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    john.smith@example.com
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    (555) 123-4567
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                Account Settings
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Typography variant="body2">
                    Email Notifications
                  </Typography>
                  <Switch defaultChecked />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Typography variant="body2">
                    SMS Alerts
                  </Typography>
                  <Switch defaultChecked />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Typography variant="body2">
                    Two-Factor Authentication
                  </Typography>
                  <Switch defaultChecked />
                </Box>
              </Box>
              
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                sx={{ mt: 3 }}
              >
                Edit Profile
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
